import { useMemo } from 'react';
import { BulkLineItem } from '../BulkAddLineItems';

export interface BulkLineItemError {
  productItemNum: string[];
  description: string[];
  glCd: string[];
  uom: string[];
  quantityReceived: string[];
}

export interface BulkLineItemWarning {
  productItemNum: string[];
  description: string[];
}

export interface BulkLineItemInfo {
  productItemNum: string[];
  description: string[];
  isTaxable: string[];
  glCd: string[];
  uom: string[];
  priceAmt: string[];
  quantityReceived: string[];
}

export const defaultBulkLineItemError: BulkLineItemError = {
  productItemNum: [],
  description: [],
  glCd: [],
  uom: [],
  quantityReceived: [],
};

export const defaultBulkLineItemWarning: BulkLineItemWarning = {
  productItemNum: [],
  description: [],
};

export const defaultBulkLineItemInfos: BulkLineItemInfo = {
  productItemNum: [],
  description: [],
  isTaxable: [],
  glCd: [],
  uom: [],
  priceAmt: [],
  quantityReceived: [],
};

const errorFields = Object.keys(defaultBulkLineItemError);
const useBulkLineItemErrors = (
  lineItems: BulkLineItem[],
  selectedLineItems?: any[],
  action: 'add' | 'edit' = 'add'
) => {
  const errors = useMemo(
    () =>
      lineItems.map((lineItem) => {
        let error: BulkLineItemError = { ...defaultBulkLineItemError };
        if (lineItem.selected) {
          if (errorFields.includes(lineItem.field)) {
            if (!lineItem.value.toString().trim()) {
              error[lineItem.field as keyof BulkLineItemError] = [`${lineItem.title} is required`];
            } else if (lineItem.field === 'productItemNum' && lineItem.value?.trim().length > 60) {
              error.productItemNum = ['Item # is more than 60 characters'];
            } else if (lineItem.field === 'description' && lineItem.value?.trim().length > 4000) {
              error.description = ['Description is more than 4000 characters'];
            } else if (lineItem.field === 'uom' && lineItem.value?.trim().length > 105) {
              error.uom = ['UOM is more than 105 characters'];
            }
          }
        }
        return error;
      }, []),
    [lineItems]
  );

  const warnings = useMemo(() => {
    let warning: BulkLineItemWarning = { ...defaultBulkLineItemWarning };
    if (selectedLineItems) {
      // Get PO line item numbers
      const poLineItemNumbers = selectedLineItems
        .filter((item) => item.canDelete !== 0)
        .map((item) => item.productItemNum || item.receiptLineItemId)
        .filter(Boolean);

      if (poLineItemNumbers.length > 0) {
        const warningMessage = `Item # and Description for following line item(s) cannot be edited because one or more selected line item(s) are part of a PO or are already invoiced/credited:\n${poLineItemNumbers
          .map((num) => `• ${num}`)
          .join('\n')}`;
        warning.productItemNum = [warningMessage];
        warning.description = [warningMessage];
      }
    }

    return warning;
  }, [selectedLineItems]);

  const infos = useMemo(
    () =>
      lineItems.map((lineItem) => {
        let info = { ...defaultBulkLineItemInfos };
        if (!lineItem.selected) {
          const infoMessage =
            action === 'edit'
              ? 'This field is currently disabled and will be left unchanged for all selected line item(s).'
              : 'This field is currently disabled and will be left empty for all new line item(s).';

          info[lineItem.field as keyof BulkLineItemInfo] = [
            infoMessage,
            'To include a value, please enable the field using the checkbox.',
          ];
        }

        return info;
      }),
    [lineItems, action]
  );

  const warningList: string[] = [
    ...Object?.values(warnings)
      .map((i) => Object.values(i)?.flat())
      .flat()
      .map((item: any) => (typeof item === 'string' ? item : Object.values(item)?.flat()))
      .flat()
      .filter((item: any, i, arr) => arr.indexOf(item) === i),
  ] as string[];
  const informationList: string[] = [
    ...Object?.values(infos)
      .map((i) => Object.values(i)?.flat())
      .flat()
      .map((item: any) => (typeof item === 'string' ? item : Object.values(item)?.flat()))
      .flat()
      .filter((item: any, i, arr) => arr.indexOf(item) === i),
  ] as string[];

  const errorList: string[] = [
    ...Object.values(errors)
      .map((i) => Object.values(i)?.flat())
      .flat()
      .map((item: any) => (typeof item === 'string' ? item : Object.values(item)?.flat()))
      .flat()
      .filter((item: any, i, arr) => arr.indexOf(item) === i),
  ] as string[];

  return { errors, warnings, infos, errorList, warningList, informationList };
};

export default useBulkLineItemErrors;
